import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Row,
} from "reactstrap";
import Header from "components/Headers/Header.js";
import Checkbox from "utils/Checkbox";
import classes from "./ManualOrder.module.css";
import {
  PRICE_LIST,
  PAYMENT_ACCOUNT_API,
  GAMES_ONLY_API,
  MANUAL_ORDER_API,
} from "../../services/CONSTANTS";
import {
  getApiDataService,
  postApiDataService,
} from "../../services/apiServices";
import { toast } from "react-toastify";
import useDebounce from "./Util/Debounce";
import ButtonLoading from "./ButtonLoading";

const SKELETON_COUNT = 4;

const ManualOrder = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [priceLoading, setPriceLoading] = useState(false);
  const [gamesLoading, setGamesLoading] = useState(false);

  const [types, setTypes] = useState(["pos"]);

  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [gameData, setGameData] = useState(null);
  const [priceList, setPriceList] = useState([]);

  const [paymentAccList, setPaymentAccList] = useState([]);
  const [paymentAccListClone, setPaymentAccListClone] = useState([]);
  const [selectedAcc, setSelectedAcc] = useState(null);

  const [gameList, setGameList] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [gameSearch, setGameSearch] = useState("");
  const debouncedGameSearch = useDebounce(gameSearch, 300);

  useEffect(() => {
    fetchPaymentAccList();
    fetchGameList();
  }, []);

  useEffect(() => {
    fetchPriceList();
  }, [selectedGame, selectedRegion]);

  useEffect(() => {
    if (selectedRegion?.country_name === "Thailand") {
      const acc = paymentAccListClone.filter((item) =>
        item.payment_provider.name.includes("SCB Bank")
      );
      setPaymentAccList(acc);
      if (
        selectedAcc &&
        !acc.some((item) => item.id === selectedAcc.id)
      ) {
        setSelectedAcc(null);
      }
    } else {
      setPaymentAccList(paymentAccListClone);
    }
  }, [selectedRegion, paymentAccListClone]);

  useEffect(() => {
    if (gameData) {
      filterPriceList(gameData);
    }
  }, [types]);

  const filteredGames = useMemo(() => {
    const query = (debouncedGameSearch || "").trim().toLowerCase();
    if (!query) return gameList;
    return gameList.filter((game) =>
      (game.name || "").toLowerCase().includes(query)
    );
  }, [gameList, debouncedGameSearch]);

  const filterPriceList = (game_data) => {
    let price_list = [];
    if (types.includes("app")) {
      price_list = price_list.concat(
        game_data.item_prices?.filter((item) => item.types.includes("app")) ||
          []
      );
    }
    if (types.includes("pos")) {
      price_list = price_list.concat(
        game_data.item_prices?.filter((item) => item.types.includes("pos")) ||
          []
      );
    }
    price_list = price_list.filter(
      (item, index, self) => index === self.findIndex((t) => t.id === item.id)
    );
    setPriceList(price_list);
  };

  const orderSubmitHandler = (e) => {
    e.preventDefault();
    const data = {
      game_id: selectedGame?.id,
      item_name: selectedItem?.item_name,
      price: selectedItem?.price_amount,
      currency: selectedItem?.region.currency_code,
      region: selectedRegion?.country_name,
      payment_account_id: selectedAcc?.id,
    };
    if (
      !data.game_id ||
      !data.item_name ||
      !data.price ||
      !data.currency ||
      !data.region ||
      !data.payment_account_id
    ) {
      toast.error(`Some fields are missing!`);
      return false;
    }

    setIsLoading(true);
    postApiDataService(MANUAL_ORDER_API, data)
      .then(() => {
        setIsLoading(false);
        setSelectedAcc(null);
        setSelectedItem(null);
      })
      .catch((err) => {
        setIsLoading(false);
        console.log("Error in orderSubmitHandler!", err);
      });
  };

  const fetchPriceList = () => {
    if (!selectedGame || !selectedRegion) return;
    setPriceLoading(true);
    setSelectedItem(null);
    getApiDataService(PRICE_LIST(selectedGame?.id, selectedRegion?.id))
      .then((res) => {
        setGameData(res.data);
        filterPriceList(res.data);
        setPriceLoading(false);
      })
      .catch((err) => {
        setPriceLoading(false);
        console.log("Error in fetchPriceList!", err);
      });
  };

  const fetchPaymentAccList = () => {
    setPaymentLoading(true);
    getApiDataService(PAYMENT_ACCOUNT_API)
      .then((res) => {
        const active_data = res.data.filter(
          (item) => item.types.includes("pos") && item.status == "active"
        );
        active_data.sort((a, b) => {
          if (a.account_name < b.account_name) {
            return -1;
          }
          return 0;
        });

        setPaymentLoading(false);
        setPaymentAccList(active_data);
        setPaymentAccListClone(active_data);
      })
      .catch((err) => {
        setPaymentLoading(false);
        console.log("Error in fetchPaymentAccList!", err);
      });
  };

  const fetchGameList = () => {
    setGamesLoading(true);
    getApiDataService(GAMES_ONLY_API)
      .then((res) => {
        setGameList(res.data);
        setSelectedGame(res.data[0]);
        setSelectedRegion(res.data[0]?.regions[0]);
        setGamesLoading(false);
      })
      .catch((err) => {
        setGamesLoading(false);
        console.log("Error in fetchGameList!", err);
      });
  };

  const priceSelectedHandler = (item) => {
    setSelectedItem(item);
  };

  const paymentSelectedHandler = (item) => {
    setSelectedAcc(item);
  };

  const gameSelectedHandler = (item) => {
    setSelectedGame(item);
    setSelectedRegion(item?.regions[0]);
    setSelectedItem(null);
  };

  const regionSelectedHandler = (item) => {
    setSelectedRegion(item);
    setSelectedItem(null);
  };

  const handleCheckboxChange = (value) => {
    if (types.includes(value)) {
      setTypes(types.filter((item) => item !== value));
    } else {
      setTypes([...types, value]);
    }
  };

  const itemLabel = (item) => {
    if (!item) return null;
    if (item.item_name === "Weekly Pass" || item.item_name === "Twilight Pass") {
      return item.item_name;
    }
    return `${gameData?.unit_name || ""}x${item.item_name}`;
  };

  const summaryItem = selectedItem
    ? itemLabel(selectedItem)
    : "Select amount";
  const summaryPrice = selectedItem
    ? `${selectedItem.region.currency_code} ${selectedItem.price_amount}`
    : "—";
  const summaryPayment = selectedAcc
    ? selectedAcc.account_name
    : "Select payment";

  const renderSkeletonCards = () =>
    Array.from({ length: SKELETON_COUNT }).map((_, index) => (
      <Col xs="12" xl="6" className="p-1" key={`skeleton-${index}`}>
        <div className="akh-skeleton-card" aria-hidden="true" />
      </Col>
    ));

  return (
    <>
      <Header />
      <Container className={`${classes.containerParent} mt--7`} fluid>
        <Row>
          <Col sm="12">
            <Card className="shadow">
              <CardHeader className="border-0">
                <h1 className="mb-3">POS</h1>

                <FormGroup className="mb-3">
                  <h4 className="mb-2">Game</h4>
                  <InputGroup className={`mb-2 ${classes.gameSearch}`}>
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText>
                        <i className="fas fa-search" />
                      </InputGroupText>
                    </InputGroupAddon>
                    <Input
                      type="search"
                      placeholder="Search games…"
                      value={gameSearch}
                      onChange={(e) => setGameSearch(e.target.value)}
                      aria-label="Search games"
                    />
                  </InputGroup>

                  {gamesLoading ? (
                    <div className={classes.gameGrid}>
                      {Array.from({ length: 6 }).map((_, index) => (
                        <div
                          key={`game-skel-${index}`}
                          className={`akh-skeleton ${classes.gameChipSkeleton}`}
                          aria-hidden="true"
                        />
                      ))}
                    </div>
                  ) : filteredGames.length === 0 ? (
                    <div className="akh-empty-state py-3">
                      <i className="ni ni-box-2" />
                      <p className="mb-0">No games match your search</p>
                    </div>
                  ) : (
                    <div
                      className={classes.gameGrid}
                      role="listbox"
                      aria-label="Games"
                    >
                      {filteredGames.map((item) => {
                        const isActive = item.id === selectedGame?.id;
                        return (
                          <button
                            type="button"
                            key={item.id}
                            role="option"
                            aria-selected={isActive}
                            className={`${classes.gameChip} ${
                              isActive ? classes.gameChipActive : ""
                            }`}
                            onClick={() => gameSelectedHandler(item)}
                          >
                            {item.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </FormGroup>

                {selectedGame?.regions?.length > 0 && (
                  <FormGroup className="mb-0">
                    <h4 className="mb-2">Region</h4>
                    <div
                      className={`akh-chip-scroll ${classes.regionScroll}`}
                      role="listbox"
                      aria-label="Regions"
                    >
                      {selectedGame.regions.map((item) => {
                        const isActive = item.id === selectedRegion?.id;
                        return (
                          <button
                            type="button"
                            key={item.id}
                            role="option"
                            aria-selected={isActive}
                            className={`${classes.regionChip} ${
                              isActive ? classes.regionChipActive : ""
                            }`}
                            onClick={() => regionSelectedHandler(item)}
                          >
                            <span>{item.country_name}</span>
                            {item.currency_code && (
                              <span className={classes.regionCurrency}>
                                {item.currency_code}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </FormGroup>
                )}
              </CardHeader>
            </Card>
          </Col>

          <Col lg="6" className={`${classes.colHalf} pr-2 pl-0`}>
            <Card className="shadow mt-3">
              <CardBody>
                <Form>
                  <FormGroup className="mb-0">
                    <div className="d-flex align-items-center justify-content-between flex-wrap">
                      <h3 className="d-block mb-2">Amount</h3>
                      <div className="d-flex align-items-center mb-2">
                        <span className="mr-3">
                          <Checkbox
                            status={types.includes("app")}
                            label="app"
                            forType="app"
                            onClick={() => handleCheckboxChange("app")}
                          />
                        </span>
                        <span>
                          <Checkbox
                            status={types.includes("pos")}
                            label="pos"
                            forType="pos"
                            onClick={() => handleCheckboxChange("pos")}
                          />
                        </span>
                      </div>
                    </div>
                    <Row>
                      {priceLoading ? (
                        renderSkeletonCards()
                      ) : priceList.length === 0 ? (
                        <Col xs="12">
                          <div className="akh-empty-state">
                            <i className="ni ni-money-coins" />
                            <p className="mb-0">
                              No POS prices for this game/region
                            </p>
                          </div>
                        </Col>
                      ) : (
                        priceList.map((item) => (
                          <Col xs="12" xl="6" className="p-1" key={item.id}>
                            <Card
                              className={`pointer shadow-lg ${
                                item?.id === selectedItem?.id
                                  ? classes.active
                                  : ""
                              } ${classes.cardHover} mb-2`}
                              style={{ cursor: "pointer" }}
                              onClick={() => priceSelectedHandler(item)}
                            >
                              <CardBody>
                                <Row className="align-items-center">
                                  <Col xs="9">
                                    <small>{item.types.toString()}</small>
                                    <p className="m-0 font-mono">
                                      {item.region.currency_code}{" "}
                                      {item.price_amount}
                                    </p>
                                    <p className="m-0">{itemLabel(item)}</p>
                                  </Col>
                                  <Col xs="3" className="p-0 text-right">
                                    <img
                                      className={classes.diamond_icon}
                                      src={item.item_image}
                                      alt=""
                                    />
                                  </Col>
                                </Row>
                              </CardBody>
                            </Card>
                          </Col>
                        ))
                      )}
                    </Row>
                  </FormGroup>
                </Form>
              </CardBody>
            </Card>
          </Col>

          <Col lg="6" className={`${classes.colHalf} pl-2 pr-0`}>
            <Card className="shadow mt-3">
              <CardBody>
                <Form>
                  <FormGroup className="mb-0">
                    <h3 className="d-block mb-2">Payment</h3>
                    <Row>
                      {paymentLoading ? (
                        renderSkeletonCards()
                      ) : paymentAccList.length === 0 ? (
                        <Col xs="12">
                          <div className="akh-empty-state">
                            <i className="ni ni-credit-card" />
                            <p className="mb-0">No payment accounts available</p>
                          </div>
                        </Col>
                      ) : (
                        paymentAccList.map((list) => (
                          <Col xs="12" xl="6" className="p-1" key={list.id}>
                            <Card
                              className={`pointer shadow-lg ${
                                classes.cardHover
                              } ${
                                list.id === selectedAcc?.id
                                  ? classes.active
                                  : ""
                              } mb-2`}
                              style={{ cursor: "pointer" }}
                              onClick={() => paymentSelectedHandler(list)}
                            >
                              <CardBody>
                                <Row className="align-items-center">
                                  <Col xs="9">
                                    <p className="m-0">{list.account_name}</p>
                                    <p className="m-0">
                                      ({list.payment_provider.name})
                                    </p>
                                    <p className="m-0 font-mono">
                                      {list.account_no}
                                    </p>
                                  </Col>
                                  <Col xs="3" className="p-0 text-right">
                                    <img
                                      className={classes.diamond_icon}
                                      src={list.payment_provider.logo}
                                      alt=""
                                    />
                                  </Col>
                                </Row>
                              </CardBody>
                            </Card>
                          </Col>
                        ))
                      )}
                    </Row>
                  </FormGroup>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <div className={classes.summaryBar} role="region" aria-label="Order summary">
          <div className={classes.summaryMeta}>
            <div className={classes.summaryField}>
              <span className={classes.summaryLabel}>Item</span>
              <span
                className={`${classes.summaryValue} ${
                  !selectedItem ? classes.summaryPlaceholder : ""
                }`}
              >
                {summaryItem}
              </span>
            </div>
            <div className={classes.summaryField}>
              <span className={classes.summaryLabel}>Price</span>
              <span
                className={`${classes.summaryValue} font-mono ${
                  !selectedItem ? classes.summaryPlaceholder : ""
                }`}
              >
                {summaryPrice}
              </span>
            </div>
            <div className={classes.summaryField}>
              <span className={classes.summaryLabel}>Payment</span>
              <span
                className={`${classes.summaryValue} ${
                  !selectedAcc ? classes.summaryPlaceholder : ""
                }`}
              >
                {summaryPayment}
              </span>
            </div>
          </div>
          <div className={classes.summaryAction}>
            <ButtonLoading
              color="primary"
              loading={isLoading}
              onClick={orderSubmitHandler}
              disabled={isLoading}
              className={classes.submitBtn}
            >
              Place order
            </ButtonLoading>
          </div>
        </div>
      </Container>
    </>
  );
};

export default ManualOrder;
